import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import AgentMessage, { AgentMsg, parsePlatformOutputs } from "./AgentMessage";
import AgentInput from "./AgentInput";
import PraiseLogo from "@/components/PraiseLogo";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

const AGENT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/praise-agent`;

const AgentChat = () => {
  const { user, profile } = useAuth();
  const [messages, setMessages] = useState<AgentMsg[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (text: string, attachments?: { type: string; data: string; name: string }[]) => {
    const userMsg: AgentMsg = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
      attachments,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    let assistantSoFar = "";

    const upsertAssistant = (nextChunk: string) => {
      assistantSoFar += nextChunk;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && !last.platformOutputs) {
          return prev.map((m, i) =>
            i === prev.length - 1 ? { ...m, content: assistantSoFar } : m
          );
        }
        return [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "assistant" as const,
            content: assistantSoFar,
            timestamp: new Date(),
          },
        ];
      });
    };

    try {
      const conversationHistory = [
        ...messages.map(m => ({ role: m.role, content: m.content })),
        { role: "user" as const, content: text },
      ];

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        toast.error("Session expired. Please sign in again.");
        setIsLoading(false);
        return;
      }

      const resp = await fetch(AGENT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          messages: conversationHistory,
          attachments: attachments?.map(a => ({ type: a.type, data: a.data })),
        }),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        if (resp.status === 429) {
          toast.error("Rate limit reached. Please wait a moment.");
        } else if (resp.status === 402) {
          toast.error("AI credits exhausted. Please upgrade your plan.");
        } else {
          toast.error(errData.error || "Something went wrong");
        }
        setIsLoading(false);
        return;
      }

      if (!resp.body) throw new Error("No response body");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) upsertAssistant(content);
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      if (textBuffer.trim()) {
        for (let raw of textBuffer.split("\n")) {
          if (!raw) continue;
          if (raw.endsWith("\r")) raw = raw.slice(0, -1);
          if (raw.startsWith(":") || raw.trim() === "") continue;
          if (!raw.startsWith("data: ")) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) upsertAssistant(content);
          } catch { /* ignore */ }
        }
      }

      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          const { cleanText, outputs } = parsePlatformOutputs(last.content);
          if (outputs.length > 0) {
            return prev.map((m, i) =>
              i === prev.length - 1
                ? { ...m, content: cleanText, platformOutputs: outputs }
                : m
            );
          }
        }
        return prev;
      });
    } catch (e) {
      console.error("Agent stream error:", e);
      toast.error("Connection failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const firstName = profile?.full_name?.split(" ")[0];

  return (
    <div className="flex flex-col h-full">
      {/* Context banner */}
      {profile?.company_name && (
        <div className="px-4 py-2 border-b border-border bg-muted/30 flex items-center gap-3">
          <span className="specimen-label">{profile.company_name}</span>
          <span className="h-3 w-px bg-border" />
          <span className="specimen-label text-brand-600">{profile.plan?.toUpperCase() || "FREE"}</span>
          {profile.preferred_platforms && profile.preferred_platforms.length > 0 && (
            <>
              <span className="h-3 w-px bg-border" />
              <span className="specimen-label">{profile.preferred_platforms.slice(0, 3).join(" · ").toUpperCase()}</span>
            </>
          )}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
        {messages.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center h-full text-center py-16 sm:py-20"
          >
            <div className="relative mb-8">
              <div className="absolute -top-8 left-1/2 w-px h-8 bg-gradient-to-b from-transparent to-brand-300" />
              <div className="w-16 h-16 rounded-2xl ink-border ink-wash flex items-center justify-center shadow-brand">
                <PraiseLogo size={32} />
              </div>
            </div>

            <span className="specimen-label block mb-3">PRAISE AGENT</span>

            <h2 className="text-xl sm:text-2xl font-editorial text-foreground mb-2">
              {firstName ? `Ready when you are, ${firstName}.` : "Ready when you are."}
            </h2>
            <p className="text-muted-foreground text-sm max-w-sm mb-8 leading-relaxed">
              Paste a testimonial, upload a screenshot, or speak —
              I'll extract ready-to-post content for your platforms.
            </p>

            <div className="flex flex-wrap gap-2 justify-center max-w-sm">
              {[
                { label: "Paste a testimonial", action: "text" },
                { label: "Upload a screenshot", action: "image" },
                { label: "Record voice", action: "voice" },
              ].map(hint => (
                <button
                  key={hint.label}
                  className="px-4 py-2 rounded-full ink-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                >
                  {hint.label}
                </button>
              ))}
            </div>

            <div className="mt-8 w-px h-12 bg-gradient-to-b from-brand-300 to-transparent" />
          </motion.div>
        )}

        {messages.map(msg => (
          <AgentMessage key={msg.id} message={msg} />
        ))}

        {/* Typing indicator */}
        {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex gap-3"
          >
            <div className="w-8 h-8 rounded-lg ink-border ink-wash flex items-center justify-center">
              <PraiseLogo size={18} />
            </div>
            <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1.5">
                <span className="w-1.5 h-1.5 bg-brand-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-brand-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 bg-brand-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <AgentInput onSend={sendMessage} isLoading={isLoading} />
    </div>
  );
};

export default AgentChat;
